"use client";

import { useUser } from "@/components/clients/LocaleToStorage";
import RouterLink from "@/components/ui/router-link";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import {
  DatasheetDownloadItem,
  fetchLatestDatasheetDownload,
  resolveDatasheetDownload,
} from "@/lib/product-datasheet";

import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";

import useSWR from "swr";

type DatasheetPropItem =
  | string
  | null
  | undefined
  | DatasheetDownloadItem<string>;

function normalizeDatasheetItem(
  item: DatasheetPropItem
): DatasheetDownloadItem<string> {
  if (item && typeof item === "object" && "file" in item) {
    return item;
  }

  return { file: item };
}

function hasDatasheetFile(items?: DatasheetPropItem[]) {
  return items?.some((item) => Boolean(normalizeDatasheetItem(item).file));
}

function openDatasheetFile(file: string, filename?: string) {
  const href = filename
    ? `${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${file}/${filename}`
    : `${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${file}`;
  const link = document.createElement("a");

  link.href = href;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = "";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function DownloadWithFileName(props: {
  file: string;
  icon: string;
  title: string;
  type?: string;
}) {
  const locale = useCurrentLocale();

  const pathname = usePathname();

  const { user } = useUser();

  const formatDate = locale === "en" ? "yyyy-MM-dd" : "yyyy年 MM月 dd日";

  const { data } = useSWR<{
    data: {
      id: string;
      filename_download: string;
      title: string;
      type: string;
      uploaded_on: string;
    };
  }>(
    () =>
      props.file
        ? `${process.env.NEXT_PUBLIC_API_HOST}/files/${props.file}`
        : null,
    async (url: string) => {
      return fetch(url).then((res) => res.json());
    }
  );

  if (!data) {
    return null;
  }

  if (!user) {
    return (
      <RouterLink
        href={`/login?redirect_url=${encodeURIComponent(pathname)}`}
        className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7"
      >
        <img
          className="h-10 w-10 md:h-[60px] md:w-[60px]"
          src={props.icon}
          alt={props.title}
        />

        <div className="w-full flex-1 pl-2 md:pl-0">
          <div
            className={cn("text-base font-medium md:pt-2 md:text-xl", {
              "md:text-lg": locale === "en",
            })}
          >
            {props.title}
          </div>

          <div className="text-xs text-[#808080] md:hidden">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>
        </div>

        <div className="flex items-center justify-between md:w-full">
          <div className="hidden text-sm text-[#808080] md:block">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>

          <div className="flex items-center space-x-2 text-primary">
            <span>PDF</span>
            <Download />
          </div>
        </div>
      </RouterLink>
    );
  }

  return (
    <a
      target="_blank"
      href={`${process.env.NEXT_PUBLIC_DIRECTUS_ASSETS}/${data.data?.id}/${data.data?.filename_download}`}
    >
      <div className="flex h-[5.5rem] w-full items-center rounded-lg bg-white px-3 py-5 shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7">
        <img
          className="h-10 w-10 md:h-[60px] md:w-[60px]"
          src={props.icon}
          alt={props.title}
        />

        <div className="w-full flex-1 pl-2 md:pl-0">
          <div
            className={cn("text-base font-medium md:pt-2 md:text-xl", {
              "md:text-lg": locale === "en",
            })}
          >
            {props.title}
          </div>

          <div className="text-xs text-[#808080] md:hidden">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>
        </div>

        <div className="flex items-center justify-between md:w-full">
          <div className="hidden text-sm text-[#808080] md:block">
            {data.data?.uploaded_on
              ? data.data.uploaded_on.substring(0, 10)
              : null}
          </div>

          <div className="flex items-center space-x-2 text-primary">
            <span>PDF</span>
            <Download />
          </div>
        </div>
      </div>
    </a>
  );
}

function DownloadDataSheet(props: {
  productId?: string | number | null;
  dataSheet: DatasheetPropItem[];
  datasheetTexts?: (string | null | undefined)[];
}) {
  const t = useScopedI18n("产品");

  const locale = useCurrentLocale();

  const pathname = usePathname();

  const { user } = useUser();
  const [isCheckingDownload, setIsCheckingDownload] = useState(false);

  const formatDate = locale === "en" ? "yyyy-MM-dd" : "yyyy年 MM月 dd日";

  const datasheets = useMemo(
    () => props.dataSheet.map(normalizeDatasheetItem),
    [props.dataSheet]
  );

  const currentDownload = useMemo(
    () => resolveDatasheetDownload(datasheets, user?.role?.level),
    [datasheets, user?.role?.level]
  );

  const currentFile = currentDownload.file;

  const currentFileName = useMemo(() => {
    const level = user?.role?.level;
    if (level) {
      return props.datasheetTexts?.[level - 1];
    }
  }, [user?.role?.level, props.datasheetTexts]);

  const { data } = useSWR<{
    data: {
      id: string;
      filename_download: string;
      title: string;
      type: string;
      uploaded_on: string;
    };
  }>(
    () =>
      currentFile
        ? `${process.env.NEXT_PUBLIC_API_HOST}/files/${currentFile}`
        : null,
    async (url: string) => {
      return fetch(url).then((res) => res.json());
    }
  );

  if (!data?.data) {
    return null;
  }

  const loginHref = `/login?redirect_url=${encodeURIComponent(pathname)}`;

  const handleDownloadClick = async () => {
    if (isCheckingDownload) {
      return;
    }

    setIsCheckingDownload(true);

    try {
      const latestDownload = props.productId
        ? await fetchLatestDatasheetDownload({
            productId: props.productId,
            pathname,
            userLevel: user?.role?.level,
          })
        : null;
      const download = latestDownload ?? currentDownload;

      if (!latestDownload && props.productId && !user) {
        window.location.href = loginHref;
        return;
      }

      if (!download.canDownload || !download.file) {
        window.location.href = loginHref;
        return;
      }

      openDatasheetFile(
        download.file,
        download.file === data.data?.id
          ? data.data?.filename_download
          : undefined
      );
    } finally {
      setIsCheckingDownload(false);
    }
  };

  return (
    <button
      type="button"
      disabled={isCheckingDownload}
      onClick={handleDownloadClick}
      className="flex h-[5.5rem] w-full appearance-none items-center rounded-lg border-0 bg-white px-3 py-5 text-left shadow-[0px_0px_6px_rgba(0,0,0,0.1)] transition-shadow duration-300 hover:shadow-[0px_0px_16px_rgba(0,0,0,0.1)] disabled:cursor-wait md:h-[190px] md:flex-col md:items-start md:justify-between md:px-8 md:py-7"
    >
      <img
        className="h-10 w-10 md:h-[60px] md:w-[60px]"
        src="/icons/datasheet.svg"
        alt="DataSheet"
      />

      <div className="w-full flex-1 pl-2 md:pl-0">
        <div
          className={cn("text-base font-medium md:pt-2 md:text-xl", {
            "md:text-lg": locale === "en",
          })}
        >
          {currentFileName ?? t("数据表")}
        </div>

        <div className="text-xs text-[#808080] md:hidden">
          {data.data?.uploaded_on
            ? data.data.uploaded_on.substring(0, 10)
            : null}
        </div>
      </div>

      <div className="flex items-center justify-between md:w-full">
        <div className="hidden text-sm text-[#808080] md:block">
          {data.data?.uploaded_on
            ? data.data.uploaded_on.substring(0, 10)
            : null}
        </div>

        <div className="flex items-center space-x-2 text-primary">
          <span>PDF</span>
          <Download />
        </div>
      </div>
    </button>
  );
}

export default function Document(props: {
  productId?: string | number | null;
  application_file?: string | null;
  datasheet?: DatasheetPropItem[];
  datasheetTexts?: (string | null | undefined)[];
  evm_file?: string | null;
}) {
  const t = useScopedI18n("产品");

  if (
    !hasDatasheetFile(props.datasheet) &&
    !props.evm_file &&
    !props.application_file
  ) {
    return <div className="pt-10 md:pt-28"></div>;
  }

  return (
    <div className="relative pt-10 md:pt-28">
      <div
        id="technical-documentation"
        className="pointer-events-none absolute -top-[46px]"
      ></div>
      <h2 className="text-center text-xl md:text-left md:text-4xl">
        {t("技术文档")}
      </h2>

      <div className="mt-5 grid grid-cols-1 gap-4 md:mt-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-7">
        {hasDatasheetFile(props.datasheet) && (
          <DownloadDataSheet
            productId={props.productId}
            dataSheet={props.datasheet ?? []}
            datasheetTexts={props.datasheetTexts}
          />
        )}

        {props.evm_file && (
          <DownloadWithFileName
            type="EVM"
            icon="/icons/evm.svg"
            title={t("EVM用户指南")}
            file={props.evm_file}
          />
        )}

        {props.application_file && (
          <DownloadWithFileName
            type="Application"
            icon="/icons/application.svg"
            title={t("产品应用手册")}
            file={props.application_file}
          />
        )}
      </div>
    </div>
  );
}
